import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useFieldOptional } from './field-context';
import { IconCalendar, IconChevronLeft, IconChevronRight } from './icons';
import { cn, disabledStyles, focusRing } from './utils';

const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;
const WEEKDAYS_LONG = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
] as const;
const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

export interface DatePickerProps {
  /** Valor em ISO `AAAA-MM-DD`. Vazio = sem data. */
  readonly value: string;
  readonly onValueChange: (isoDate: string) => void;
  readonly disabled?: boolean;
  readonly min?: string;
  readonly max?: string;
  readonly className?: string;
  readonly ariaLabel?: string;
  readonly placeholder?: string;
}

/**
 * Seletor de data pt-BR.
 *
 * CONTRATO DE DADO: o VALOR trafega sempre em ISO `AAAA-MM-DD`; a EXIBIÇÃO é
 * sempre `dd/mm/aaaa`. Essa separação é obrigatória — architecture.md §5 proíbe
 * `VARCHAR 'DD/MM/AAAA'` (achado C8 do legado, que gravava a data como texto
 * brasileiro e depois precisava de `STR_TO_DATE` em toda query).
 *
 * ACESSIBILIDADE:
 *  · o campo de texto aceita digitação direta em dd/mm/aaaa e é o caminho
 *    primário — o calendário é atalho, não requisito. Quem usa leitor de tela
 *    digita a data e nunca precisa abrir a grade;
 *  · a grade é `role="grid"` com navegação por setas, PageUp/PageDown para
 *    mês e Shift+PageUp/PageDown para ano (padrão APG Date Picker Dialog);
 *  · cada célula tem `aria-label` com a data por extenso, porque "14" sozinho
 *    não diz de que mês.
 */
export function DatePicker({
  value,
  onValueChange,
  disabled = false,
  min,
  max,
  className,
  ariaLabel,
  placeholder = 'dd/mm/aaaa',
}: DatePickerProps) {
  const field = useFieldOptional();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => isoToBr(value));
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(parseIso(value) ?? new Date()));
  const gridRef = useRef<HTMLDivElement>(null);
  const [focusedDate, setFocusedDate] = useState(() => parseIso(value) ?? today());

  useEffect(() => {
    setText(isoToBr(value));
    const parsed = parseIso(value);
    if (parsed) {
      setViewMonth(startOfMonth(parsed));
      setFocusedDate(parsed);
    }
  }, [value]);

  const weeks = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);

  const commitText = (raw: string) => {
    const iso = brToIso(raw);
    if (iso === null) {
      // Texto incompleto: mantemos o que o usuário digitou e não apagamos o
      // valor — apagar em cada tecla impediria digitar "1" de "14/03/2026".
      return;
    }
    onValueChange(iso);
  };

  const selectDate = (date: Date) => {
    const iso = toIso(date);
    if (min && iso < min) return;
    if (max && iso > max) return;
    onValueChange(iso);
    setText(isoToBr(iso));
    setOpen(false);
  };

  const handleGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const move = (days: number) => {
      event.preventDefault();
      const next = addDays(focusedDate, days);
      setFocusedDate(next);
      setViewMonth(startOfMonth(next));
    };

    switch (event.key) {
      case 'ArrowRight':
        move(1);
        break;
      case 'ArrowLeft':
        move(-1);
        break;
      case 'ArrowDown':
        move(7);
        break;
      case 'ArrowUp':
        move(-7);
        break;
      case 'Home': {
        event.preventDefault();
        const start = addDays(focusedDate, -focusedDate.getDay());
        setFocusedDate(start);
        break;
      }
      case 'End': {
        event.preventDefault();
        const end = addDays(focusedDate, 6 - focusedDate.getDay());
        setFocusedDate(end);
        break;
      }
      case 'PageUp': {
        event.preventDefault();
        const next = addMonths(focusedDate, event.shiftKey ? -12 : -1);
        setFocusedDate(next);
        setViewMonth(startOfMonth(next));
        break;
      }
      case 'PageDown': {
        event.preventDefault();
        const next = addMonths(focusedDate, event.shiftKey ? 12 : 1);
        setFocusedDate(next);
        setViewMonth(startOfMonth(next));
        break;
      }
      case 'Enter':
      case ' ':
        event.preventDefault();
        selectDate(focusedDate);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!open) return;
    const node = gridRef.current?.querySelector<HTMLElement>('[data-focused="true"]');
    node?.focus();
  }, [open, focusedDate]);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          'flex h-[2.375rem] items-center rounded-md border border-border-control bg-surface pr-1',
          'focus-within:ring-2 focus-within:ring-focus focus-within:ring-offset-2 focus-within:ring-offset-canvas',
          field?.hasError && 'border-danger ring-1 ring-danger',
          disabled && 'cursor-not-allowed opacity-55',
          className,
        )}
      >
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={text}
          {...field?.controlProps}
          aria-label={field ? undefined : ariaLabel}
          onChange={(event) => {
            const masked = maskBrDate(event.target.value);
            setText(masked);
            commitText(masked);
          }}
          onBlur={() => {
            // No blur, texto inválido volta ao último valor válido em vez de
            // ficar num estado ambíguo.
            const iso = brToIso(text);
            if (iso === null) setText(isoToBr(value));
          }}
          className={cn(
            'min-w-0 flex-1 bg-transparent px-3 text-base text-content outline-none qc-tabular',
            'placeholder:text-content-subtle',
            disabledStyles,
          )}
        />
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Abrir calendário"
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded text-content-subtle',
              'hover:bg-surface-sunken hover:text-content',
              focusRing,
            )}
          >
            <IconCalendar size={16} />
          </button>
        </PopoverPrimitive.Trigger>
      </div>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-dropdown rounded-lg border border-border bg-surface-raised p-3 shadow-lg animate-content-in"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              aria-label="Mês anterior"
              onClick={() => setViewMonth(addMonths(viewMonth, -1))}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded text-content-subtle hover:bg-surface-sunken hover:text-content',
                focusRing,
              )}
            >
              <IconChevronLeft size={16} />
            </button>
            <p aria-live="polite" className="text-sm font-semibold capitalize text-content">
              {MONTHS[viewMonth.getMonth()]} de {viewMonth.getFullYear()}
            </p>
            <button
              type="button"
              aria-label="Próximo mês"
              onClick={() => setViewMonth(addMonths(viewMonth, 1))}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded text-content-subtle hover:bg-surface-sunken hover:text-content',
                focusRing,
              )}
            >
              <IconChevronRight size={16} />
            </button>
          </div>

          <div
            ref={gridRef}
            role="grid"
            aria-label={`Calendário de ${MONTHS[viewMonth.getMonth()]} de ${viewMonth.getFullYear()}`}
            onKeyDown={handleGridKeyDown}
          >
            <div role="row" className="grid grid-cols-7">
              {WEEKDAYS_SHORT.map((day, index) => (
                <div
                  key={day}
                  role="columnheader"
                  aria-label={WEEKDAYS_LONG[index]}
                  className="py-1 text-center text-2xs font-semibold uppercase text-content-subtle"
                >
                  {day}
                </div>
              ))}
            </div>

            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} role="row" className="grid grid-cols-7">
                {week.map((date) => {
                  const iso = toIso(date);
                  const inMonth = date.getMonth() === viewMonth.getMonth();
                  const isSelected = iso === value;
                  const isToday = iso === toIso(today());
                  const isFocused = iso === toIso(focusedDate);
                  const outOfRange = Boolean((min && iso < min) || (max && iso > max));

                  return (
                    <div role="gridcell" key={iso} aria-selected={isSelected}>
                      <button
                        type="button"
                        tabIndex={isFocused ? 0 : -1}
                        data-focused={isFocused || undefined}
                        disabled={outOfRange}
                        onClick={() => selectDate(date)}
                        aria-label={longDateLabel(date)}
                        aria-current={isToday ? 'date' : undefined}
                        className={cn(
                          'flex h-8 w-9 items-center justify-center rounded text-sm qc-tabular',
                          inMonth ? 'text-content' : 'text-content-subtle',
                          isSelected && 'bg-brand font-semibold text-brand-contrast',
                          !isSelected && isToday && 'ring-1 ring-brand',
                          !isSelected && !outOfRange && 'hover:bg-surface-sunken',
                          outOfRange && 'cursor-not-allowed opacity-40',
                          focusRing,
                        )}
                      >
                        {date.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="mt-2 flex justify-between gap-2 border-t border-border-subtle pt-2">
            <button
              type="button"
              onClick={() => selectDate(today())}
              className={cn('rounded px-2 py-1 text-xs font-medium text-brand hover:underline', focusRing)}
            >
              Hoje
            </button>
            <button
              type="button"
              onClick={() => {
                onValueChange('');
                setText('');
                setOpen(false);
              }}
              className={cn(
                'rounded px-2 py-1 text-xs font-medium text-content-muted hover:underline',
                focusRing,
              )}
            >
              Limpar
            </button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

// ── Utilidades de data (locais, sem fuso) ───────────────────────────────────
// Datas puras (`revisionDate`, `validUntil`) NÃO têm hora nem fuso — são DATE
// no banco (architecture.md §5). Manipular como `Date` local aqui é seguro
// porque nunca convertemos para UTC: só formatamos e reserializamos ISO.

function today(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function addMonths(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay));
}

export function toIso(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseIso(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);

  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;

  /*
   * O construtor do `Date` NORMALIZA em silêncio: `new Date(2026, 1, 29)` não
   * falha, vira 1º de março. Sem esta conferência, uma data impossível vinda do
   * servidor ou digitada pelo usuário era EXIBIDA como outra data — e, num
   * sistema em que data de revisão, prazo e data do fato são registro regulado,
   * substituir 29/02 por 01/03 sem avisar é adulteração silenciosa.
   *
   * Só é válida a data que sobrevive à ida e à volta.
   */
  const rolou =
    date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day;
  return rolou ? null : date;
}

/** ISO → exibição brasileira. String vazia quando não há data. */
export function isoToBr(iso: string): string {
  const date = parseIso(iso);
  if (!date) return '';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

/** Exibição brasileira → ISO. `null` quando a data é incompleta ou inexistente. */
export function brToIso(br: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br.trim());
  if (!match) return null;
  const [, d, m, y] = match;
  const day = Number(d);
  const month = Number(m);
  const year = Number(y);
  if (month < 1 || month > 12) return null;
  const lastDay = new Date(year, month, 0).getDate();
  if (day < 1 || day > lastDay) return null;
  return `${String(year).padStart(4, '0')}-${m}-${d}`;
}

/** Aplica a máscara dd/mm/aaaa conforme o usuário digita. */
function maskBrDate(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function longDateLabel(date: Date): string {
  return `${WEEKDAYS_LONG[date.getDay()]}, ${date.getDate()} de ${MONTHS[date.getMonth()]} de ${date.getFullYear()}`;
}

function buildMonthGrid(month: Date): Date[][] {
  const firstOfMonth = startOfMonth(month);
  const start = addDays(firstOfMonth, -firstOfMonth.getDay());
  const weeks: Date[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const days: Date[] = [];
    for (let day = 0; day < 7; day += 1) {
      days.push(addDays(start, week * 7 + day));
    }
    weeks.push(days);
  }
  return weeks;
}
